import { inject, Injectable } from '@angular/core';
import {
  EPricingRuleType,
  EQuantityCalculation,
  getTicketPricingRuleBracket,
  ICalcProductPricingRule
} from '@roller/booking-calculation-service';
import { DateFormatService } from '../date/date-format.service';
import { IProductSession } from '../product/models/product-session.model';
import { ProductHelperService } from '../product/product-helper.service';
import { IVenueConfig } from '../venue-config/models/venue-config.model';
import { IOrderItem } from './models/order-item.model';

@Injectable({
  providedIn: 'root'
})
export class OrderPricingRuleService {
  private productHelperService = inject(ProductHelperService);
  private dateFormatService = inject(DateFormatService);

  /** Adds extra data for the booking calculation service to determine the item price */
  getItemsWithPricingRuleData(
    orderItems: IOrderItem[],
    venueConfig: IVenueConfig,
    bookingCreatedDate: Date | undefined
  ): IOrderItem[] {
    return orderItems.map((item) => {
      if (!item.product.pricingRule) {
        return item;
      }

      switch (item.product.pricingRule.type) {
        case EPricingRuleType.quantity: {
          item.pricingRule = this.getItemPricingRule(item.product.pricingRule, orderItems, item);

          return {
            ...item,
            pricingRuleBracket: getTicketPricingRuleBracket(item)
          };
        }
        case EPricingRuleType.time: {
          return {
            ...item,
            sessionStart: item.session ? this.dateFormatService.hourStringToNumber(item.session.startTime) : undefined
          };
        }
        case EPricingRuleType.earlyBird: {
          const timezoneOffset: number = venueConfig.venueSettings.timeZoneOffsetMinutes ?? 0;
          const currentVenueTime = this.dateFormatService.currentVenueTime(timezoneOffset);

          return {
            ...item,
            venueTime: currentVenueTime,
            bookingCreatedDate: bookingCreatedDate
          };
        }
        default:
          return item;
      }
    });
  }

  /** Calculates the total quantity for a parent product/session combination. Used for pricing rule bracket calculations. */
  getProductTotalQuantity(
    items: IOrderItem[],
    parentProductId: string,
    bookingDate: Date,
    session?: IProductSession
  ): number {
    return items.reduce((total, orderItem) => {
      if (this.isSameParentProduct(orderItem, parentProductId, bookingDate, session)) {
        return total + orderItem.quantity;
      }

      return total;
    }, 0);
  }

  /** Calculates the total quantity of tickets in the basket used to determine the current pricing rule bracket. */
  getPerProductAmount(items: IOrderItem[], item: IOrderItem): number | undefined {
    if (
      item.pricingRule?.quantityCalculation !== EQuantityCalculation.perProduct &&
      item.pricingRule?.quantityCalculation !== EQuantityCalculation.ticketTotal
    ) {
      return undefined;
    }

    return this.getProductTotalQuantity(items, item.parentProductId, item.bookingDate, item.session);
  }

  /** Checks if an order item belongs to the same parent product and session. */
  private isSameParentProduct(
    orderItem: IOrderItem,
    parentProductId: string,
    bookingDate: Date,
    session?: IProductSession
  ): boolean {
    return (
      orderItem.parentProductId === parentProductId &&
      this.dateFormatService.isSameDay(orderItem.bookingDate, bookingDate) &&
      (!session || this.productHelperService.isSameSession(orderItem.session, session))
    );
  }

  private getItemPricingRule(
    pricingRule: ICalcProductPricingRule,
    orderItems: IOrderItem[],
    item: IOrderItem
  ): ICalcProductPricingRule {
    return {
      ...pricingRule,
      perProductAmount: this.getPerProductAmount(orderItems, item)
    };
  }
}
